<template>
  <TopAuthView
    :announcements="announcements"
    :agent-recommend-items="agentRecommendItems"
    :message-tabs="messageTabs"
    :message-items-by-tab="messageItemsByTab"
    :message-empty-state-by-tab="messageEmptyStateByTab"
    :favorite-tabs="favoriteTabs"
    :favorite-items-by-tab="favoriteItemsByTab"
    :jobs-tabs="jobsTabs"
    :jobs-items-by-tab="jobsItemsByTab"
    :should-show-steps-registration="shouldShowStepsRegistration"
    :user-platform="userPlatform"
    :dashboard-loading="dashboardLoading"
    :favorite-section-loading-by-tab="favoriteSectionLoadingByTab"
    :show-eeasy-my-page-guide-modal="showEeasyMyPageGuideModal"
    @registration-completed="shouldShowStepsRegistration = false"
    @update:show-eeasy-my-page-guide-modal="showEeasyMyPageGuideModal = $event"
  />
</template>

<script setup lang="ts">
import type {
  DashboardData,
  DashboardJobItem,
  DashboardMatchingFlow,
  DashboardRecentlyJob,
} from '@/apis/dashboard';
import type { DirectJob } from '@/apis/direct';
import type { CarouselItem } from '@/components/shared/carousel/types';
import type { MessageSummaryCardProps } from '@/components/shared/message/types';
import type { FilledTabItem } from '@/components/shared/tab/filled-tab/types';
import type { AgentRecommendationItem } from '@/components/top/auth/agent-recommend/types';
import type { UserPlatform } from '@/components/top/auth/types';
import type { AnnouncementProps } from '@/types/announcement';

import { getProfile } from '@/apis/candidate';
import { getConsiderationJobs } from '@/apis/consideration.api';
import { getDashboard } from '@/apis/dashboard.api';
import { getFavoriteMatched, getFavoriteReceived, getFavoriteSent } from '@/apis/favorites.api';
import TopAuthView from '@/components/top/auth/TopAuthView.vue';
import {
  applyIsPreCounselingFromConsiderationData,
  useTopAuthInteractions,
} from '@/composables/useTopAuthInteractions';
import {
  createTopAuthCardMappers,
  mapAgentConsiderationToCarouselItem,
  mapAnnouncementFromDashboard,
  mapDirectFavoriteToCarouselItem,
  mapFavoriteToCarouselItem,
  mapMatchingFlowToMessageCard,
  mapSelectionPropositionToMessageCard,
} from '@/utils/topAuthMappers';
import {
  buildAgentSelectionEmptyState,
  buildScoutSelectionEmptyState,
} from '@/utils/messages/emptyStateCta';
import { computed, onMounted, ref } from 'vue';

/** API の platform を UserPlatform に正規化 */
function toUserPlatform(value: unknown): UserPlatform | undefined {
  const s = value == null ? '' : String(value).toLowerCase();
  if (s === 'agent') return 'agent';
  if (s === 'direct') return 'direct';
  if (s === 'both') return 'both';
  return undefined;
}

const dashboardLoading = ref(true);
const favoriteSectionLoadingByTab = ref<Record<'favorites' | 'considering', boolean>>({
  favorites: false,
  considering: false,
});
const announcements = ref<AnnouncementProps[]>([]);
const agentRecommendItems = ref<AgentRecommendationItem[]>([]);
const messageItemsByTab = ref<Record<string, MessageSummaryCardProps[]>>({
  direct: [],
  agent: [],
});
const favoriteItemsByTab = ref<Record<string, CarouselItem[]>>({
  favorites: [],
  considering: [],
});
const jobsItemsByTab = ref<Record<string, CarouselItem[]>>({
  scout: [],
  agent: [],
  history: [],
});
const rawRecommendedJobs = ref<DashboardJobItem[]>([]);
const rawDirectJobs = ref<DashboardJobItem[]>([]);
const rawAgentJobs = ref<DashboardJobItem[]>([]);
const rawRecentlyJobs = ref<DashboardRecentlyJob[]>([]);
const rawFavoriteItems = ref<CarouselItem[]>([]);
const rawConsideringItems = ref<CarouselItem[]>([]);
const hasAvailableScouts = ref(false);

/** ダッシュボード取得後にのみ設定。未取得時は undefined でモーダルは初期化しない */
const shouldShowStepsRegistration = ref<boolean | undefined>(undefined);

/** プロフィールの platform（ActionList の表示条件に使用）。未取得時は undefined */
const userPlatform = ref<UserPlatform | undefined>(undefined);

/** マイページ初回表示時、eeasy 誘導モーダル（カウ前: is_pre_counseling === true のエージェント利用者） */
const showEeasyMyPageGuideModal = ref(false);

const messageTabs: FilledTabItem[] = [
  { id: 'direct', label: '企業スカウト' },
  { id: 'agent', label: 'エージェント' },
];

const favoriteTabs: FilledTabItem[] = [
  { id: 'favorites', label: '気になる求人リスト' },
  { id: 'considering', label: '検討中の求人リスト' },
];

const jobsTabs: FilledTabItem[] = [
  { id: 'scout', label: '企業スカウト求人' },
  { id: 'agent', label: 'エージェント求人' },
  { id: 'history', label: '閲覧履歴' },
];
const FAVORITE_SECTION_LIMIT = 10;

const {
  considerationByJobId,
  favoriteByJobId,
  isPreCounseling,
  refreshConsiderationState,
  refreshFavoriteState,
  handleToggleConsideration,
  handleToggleFavorite,
} = useTopAuthInteractions({
  onConsiderationUpdated: async () => {
    await refreshConsideringSectionItems();
    syncInteractiveCards();
  },
  onFavoriteUpdated: async () => {
    await refreshFavoriteSectionItems();
    syncInteractiveCards();
  },
});
const messageEmptyStateByTab = computed(() => ({
  direct: buildScoutSelectionEmptyState(hasAvailableScouts.value),
  agent: buildAgentSelectionEmptyState(isPreCounseling.value),
}));

const {
  mapDashboardJobToRecommendItem,
  mapMatchingFlowToCarouselItem,
  mapDashboardJobToCarouselItem,
  mapRecentlyJobToCarouselItem,
} = createTopAuthCardMappers({
  getConsiderationState: (jobId: number) => considerationByJobId.value[jobId],
  getFavoriteState: (jobId: number) => favoriteByJobId.value[jobId],
  onToggleConsideration: handleToggleConsideration,
  onToggleFavorite: handleToggleFavorite,
});

function syncInteractiveCards(): void {
  agentRecommendItems.value = rawRecommendedJobs.value.map(mapDashboardJobToRecommendItem);
  favoriteItemsByTab.value = {
    favorites: rawFavoriteItems.value,
    considering: rawConsideringItems.value,
  };
  jobsItemsByTab.value = {
    scout: rawDirectJobs.value.map(mapDashboardJobToCarouselItem),
    agent: rawAgentJobs.value.map(mapDashboardJobToCarouselItem),
    history: rawRecentlyJobs.value.map(mapRecentlyJobToCarouselItem),
  };
}

/** エージェント利用かつカウ前（is_pre_counseling === true）のとき eeasy 誘導を表示 */
function shouldShowEeasyMyPageGuide(
  platform: UserPlatform | undefined,
  flag: boolean | null | undefined
): boolean {
  if (platform !== 'agent' && platform !== 'both') return false;
  return flag === true;
}

async function refreshFavoriteSectionItems(): Promise<void> {
  favoriteSectionLoadingByTab.value = {
    ...favoriteSectionLoadingByTab.value,
    favorites: true,
  };
  try {
    const [receivedResult, sentResult, matchedResult] = await Promise.allSettled([
      getFavoriteReceived({ page: 1 }),
      getFavoriteSent({ page: 1 }),
      getFavoriteMatched({ page: 1 }),
    ]);

    const favoriteEntries: DirectJob[] = [];
    if (receivedResult.status === 'fulfilled') {
      favoriteEntries.push(...(receivedResult.value?.data?.entries ?? []));
    }
    if (sentResult.status === 'fulfilled') {
      favoriteEntries.push(...(sentResult.value?.data?.entries ?? []));
    }
    if (matchedResult.status === 'fulfilled') {
      favoriteEntries.push(...(matchedResult.value?.data?.entries ?? []));
    }
    const hasFavoriteFetchResult =
      receivedResult.status === 'fulfilled' ||
      sentResult.status === 'fulfilled' ||
      matchedResult.status === 'fulfilled';
    if (!hasFavoriteFetchResult) return;
    rawFavoriteItems.value = favoriteEntries
      .slice(0, FAVORITE_SECTION_LIMIT)
      .map(mapDirectFavoriteToCarouselItem);
  } finally {
    favoriteSectionLoadingByTab.value = {
      ...favoriteSectionLoadingByTab.value,
      favorites: false,
    };
  }
}

async function refreshConsideringSectionItems(): Promise<void> {
  favoriteSectionLoadingByTab.value = {
    ...favoriteSectionLoadingByTab.value,
    considering: true,
  };
  try {
    const response = await getConsiderationJobs();
    applyIsPreCounselingFromConsiderationData(isPreCounseling, response?.data);
    const considerationEntries = Array.isArray(response?.data?.entries)
      ? response.data.entries
      : [];
    rawConsideringItems.value = considerationEntries
      .slice(0, FAVORITE_SECTION_LIMIT)
      .map(mapAgentConsiderationToCarouselItem);
  } finally {
    favoriteSectionLoadingByTab.value = {
      ...favoriteSectionLoadingByTab.value,
      considering: false,
    };
  }
}

onMounted(async () => {
  // プロフィールから platform を取得（ActionList の表示条件に使用）
  try {
    const profileRes = await getProfile();
    const payload = profileRes?.data ?? profileRes ?? {};
    const profile =
      (payload as { candidate?: { platform?: unknown } }).candidate ??
      (payload as { profile?: { platform?: unknown } }).profile ??
      payload;
    const raw = (profile as { platform?: unknown }).platform;
    const normalized = toUserPlatform(raw);
    if (normalized) userPlatform.value = normalized;
  } catch {
    // 未ログインなどで取得失敗時は userPlatform は undefined のまま
  }

  try {
    const dashboardRes = await getDashboard();
    const isSuccess = dashboardRes && Number(dashboardRes.code) === 1;
    const data =
      typeof dashboardRes?.data === 'object' && dashboardRes.data !== null
        ? dashboardRes.data
        : typeof (dashboardRes as { direct_jobs?: unknown })?.direct_jobs !== 'undefined'
        ? (dashboardRes as unknown as Record<string, unknown>)
        : null;

    if (isSuccess && data) {
      shouldShowStepsRegistration.value =
        (data as { should_show_steps_registration?: boolean }).should_show_steps_registration ??
        false;

      const maintenance =
        (data as { maintenance_announcements?: { id: number; title: string }[] })
          .maintenance_announcements ?? [];
      const general =
        (data as { general_announcements?: { id: number; title: string }[] })
          .general_announcements ?? [];
      announcements.value = [...maintenance, ...general].map(mapAnnouncementFromDashboard);

      const typedData = data as DashboardData;
      hasAvailableScouts.value = typedData.has_available_scouts ?? false;
      if (typedData.is_pre_counseling !== undefined) {
        isPreCounseling.value = typedData.is_pre_counseling;
      }

      const validRecommended = (typedData.recommended_jobs ?? []).filter(
        (item): item is DashboardJobItem =>
          !!item && typeof item === 'object' && !!item.job && !!item.company
      );
      rawRecommendedJobs.value = validRecommended;

      // 選考管理セクション: 企業スカウトタブ = selection_propositions、エージェントタブ = matching_flows
      const selectionPropositions = typedData.selection_propositions ?? [];
      const matchingFlows = typedData.matching_flows ?? [];
      messageItemsByTab.value = {
        direct: selectionPropositions.map(mapSelectionPropositionToMessageCard),
        agent: matchingFlows.map((m) => mapMatchingFlowToMessageCard(m, true)),
      };

      const validConsideration = (typedData.consideration_matching_flows ?? []).filter(
        (m): m is DashboardMatchingFlow => !!m?.job && m.job.id != null && m.job.name != null
      );
      rawFavoriteItems.value = (typedData.favorite_propositions ?? []).map(
        mapFavoriteToCarouselItem
      );
      rawConsideringItems.value = validConsideration.map(mapMatchingFlowToCarouselItem);

      const dataRecord = data as Record<string, unknown>;
      rawRecentlyJobs.value = (
        Array.isArray(dataRecord['recently_jobs']) ? dataRecord['recently_jobs'] : []
      ) as DashboardRecentlyJob[];
      rawDirectJobs.value = typedData.direct_jobs ?? [];
      rawAgentJobs.value = typedData.agent_jobs ?? [];

      await Promise.all([refreshConsiderationState(), refreshFavoriteState()]);
      syncInteractiveCards();
      if (shouldShowEeasyMyPageGuide(userPlatform.value, isPreCounseling.value)) {
        showEeasyMyPageGuideModal.value = true;
      }
    } else {
      shouldShowStepsRegistration.value = false;
    }
  } catch {
    shouldShowStepsRegistration.value = false;
  } finally {
    dashboardLoading.value = false;
  }
});
</script>
